'use client';

import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Button,
  Checkbox,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@predoc/cow';

// ─── Types ───────────────────────────────────────────────────────────────────

export type TreeNode<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  id: string;
  label: string;
  childNodes?: TreeNode<T>[];
  disabled?: boolean;
} & T;

export type TreeViewProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  data: TreeNode<T>[];
  selectedIds?: string[];
  defaultSelectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  defaultExpandedIds?: string[];
  renderLabel?: (node: TreeNode<T>) => React.ReactNode;
  className?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Collect all leaf node ids from a tree node recursively, skipping disabled nodes. */
const getAllLeafIds = <T extends Record<string, unknown>>(
  node: TreeNode<T>,
): string[] => {
  if (node.disabled) return [];
  if (!node.childNodes?.length) return [node.id];
  return node.childNodes.flatMap((child) => getAllLeafIds(child));
};

/** Determine the checked state for a node given the selected set. */
const getCheckedState = <T extends Record<string, unknown>>(
  node: TreeNode<T>,
  selectedIds: Set<string>,
): boolean | 'indeterminate' => {
  if (!node.childNodes?.length) return selectedIds.has(node.id);

  const leafIds = getAllLeafIds(node);
  const selectedCount = leafIds.filter((id) => selectedIds.has(id)).length;

  if (selectedCount === 0) return false;
  if (selectedCount === leafIds.length) return true;
  return 'indeterminate';
};

// ─── TreeNodeItem ────────────────────────────────────────────────────────────

type TreeNodeItemProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  node: TreeNode<T>;
  depth: number;
  selectedIds: Set<string>;
  onToggleNode: (node: TreeNode<T>) => void;
  defaultExpandedIds?: Set<string>;
  renderLabel?: (node: TreeNode<T>) => React.ReactNode;
};

const TreeNodeItem = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  node,
  depth,
  selectedIds,
  onToggleNode,
  defaultExpandedIds,
  renderLabel,
}: TreeNodeItemProps<T>) => {
  const hasChildren = !!node.childNodes?.length;
  const checkedState = getCheckedState(node, selectedIds);

  const nodeContent = (
    <div
      data-slot="tree-node"
      role="treeitem"
      aria-selected={checkedState === true}
      className={cn(
        'flex items-center gap-2 py-1.5',
        node.disabled && 'opacity-50',
      )}
      style={{ paddingLeft: depth * 24 }}
    >
      {hasChildren ? (
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="group size-6 shrink-0"
            aria-label={`Toggle ${node.label}`}
          >
            <ChevronRight className="text-muted-foreground size-4 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          </Button>
        </CollapsibleTrigger>
      ) : (
        <span className="inline-flex size-6 shrink-0" aria-hidden="true" />
      )}

      <Checkbox
        checked={checkedState}
        onCheckedChange={() => onToggleNode(node)}
        disabled={node.disabled}
        aria-label={`Select ${node.label}`}
      />

      <span
        className={cn(
          'text-sm leading-none font-medium select-none',
          node.disabled && 'text-muted-foreground',
        )}
      >
        {renderLabel ? renderLabel(node) : node.label}
      </span>
    </div>
  );

  if (!hasChildren) {
    return nodeContent;
  }

  const isDefaultExpanded = defaultExpandedIds?.has(node.id) ?? false;

  return (
    <Collapsible defaultOpen={isDefaultExpanded}>
      {nodeContent}
      <CollapsibleContent role="group">
        {node.childNodes!.map((child) => (
          <TreeNodeItem
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedIds={selectedIds}
            onToggleNode={onToggleNode}
            defaultExpandedIds={defaultExpandedIds}
            renderLabel={renderLabel}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

// ─── TreeView ────────────────────────────────────────────────────────────────

const TreeView = <T extends Record<string, unknown> = Record<string, unknown>>({
  data,
  selectedIds: controlledSelectedIds,
  defaultSelectedIds = [],
  onSelectionChange,
  defaultExpandedIds = [],
  renderLabel,
  className,
}: TreeViewProps<T>) => {
  const [internalSelectedIds, setInternalSelectedIds] = React.useState<
    Set<string>
  >(new Set(defaultSelectedIds));

  const isControlled = controlledSelectedIds !== undefined;
  const selectedIds = React.useMemo(
    () => (isControlled ? new Set(controlledSelectedIds) : internalSelectedIds),
    [isControlled, controlledSelectedIds, internalSelectedIds],
  );

  const defaultExpandedSet = React.useMemo(
    () => new Set(defaultExpandedIds),
    [defaultExpandedIds],
  );

  const handleToggleNode = React.useCallback(
    (node: TreeNode<T>) => {
      const leafIds = getAllLeafIds(node);
      const currentChecked = getCheckedState(node, selectedIds);
      const nextSelected = new Set(selectedIds);

      if (currentChecked === true) {
        leafIds.forEach((id) => nextSelected.delete(id));
      } else {
        leafIds.forEach((id) => nextSelected.add(id));
      }

      if (!isControlled) {
        setInternalSelectedIds(nextSelected);
      }
      onSelectionChange?.(Array.from(nextSelected));
    },
    [selectedIds, isControlled, onSelectionChange],
  );

  return (
    <div data-slot="tree-view" className={className} role="tree">
      {data.map((node) => (
        <TreeNodeItem
          key={node.id}
          node={node}
          depth={0}
          selectedIds={selectedIds}
          onToggleNode={handleToggleNode}
          defaultExpandedIds={defaultExpandedSet}
          renderLabel={renderLabel}
        />
      ))}
    </div>
  );
};

export { TreeView };
